import { USE_CASE_DEPTH } from './useCaseDepth'

export interface UseCaseData {
  slug: string
  title: string
  subtitle: string
  description: string
  category: 'business' | 'industry' | 'form-type' | 'business-type' | 'link-page'
  icon: string
  problemTitle: string
  problemDescription: string
  solutionTitle: string
  solutionDescription: string
  benefits: {
    title: string
    description: string
  }[]
  exampleFields: string[]
  features: string[]
  strategies?: {
    title: string
    description: string
  }[]
  /**
   * 250-350 words of slug-specific prose.
   *
   * Measured before this existed: 27 use-case pages averaged 431 body words but
   * only 170 *unique* to the page — the thinnest family on the site by 2x
   * (/for 588, /templates 517, /compare 398). Sorting by unique words put the
   * 15 slugs without `strategies` at ranks 1-15 and the 12 with it at 16-27, a
   * perfect correlation: the shape of the data was the ceiling. That thinness
   * is the most likely reason so many of these sit in "Crawled - currently not
   * indexed", which is a quality verdict rather than a crawling problem.
   */
  deepDive?: string
  /** 4 question/answer pairs. Also feeds FAQPage structured data. */
  faq?: {
    q: string
    a: string
  }[]
}

export const useCasesData: UseCaseData[] = [
  // Business Use Cases
  {
    slug: 'contact-forms',
    title: 'Contact Forms',
    subtitle: 'Capture customer inquiries effortlessly',
    description: 'Create professional contact forms that make it easy for visitors to reach out to your business.',
    category: 'business',
    icon: 'EnvelopeIcon',
    problemTitle: 'Missing Customer Inquiries?',
    problemDescription: 'Without a proper contact form, potential customers may leave your site without reaching out. Email addresses get spammed, and phone calls get missed.',
    solutionTitle: 'Professional Contact Forms in Minutes',
    solutionDescription: 'Create beautiful, mobile-friendly contact forms that capture every inquiry. Get instant notifications and never miss a lead.',
    benefits: [
      { title: 'Instant Notifications', description: 'Get notified immediately when someone fills out your form' },
      { title: 'Spam Protection', description: 'Built-in spam filtering keeps your inbox clean' },
      { title: 'Mobile Friendly', description: 'Forms work perfectly on any device' },
      { title: 'Easy Embedding', description: 'Add to any website with a simple code snippet' },
    ],
    exampleFields: ['Full Name', 'Email Address', 'Phone Number', 'Subject', 'Message'],
    features: ['Custom fields', 'File attachments', 'Auto-responders', 'CRM integration'],
  },
  {
    slug: 'lead-generation',
    title: 'Lead Generation',
    subtitle: 'Convert visitors into qualified leads',
    description: 'Build high-converting lead capture forms that grow your sales pipeline.',
    category: 'business',
    icon: 'UserPlusIcon',
    problemTitle: 'Losing Potential Customers?',
    problemDescription: 'Every visitor who leaves without providing their contact info is a missed opportunity. Generic forms with low conversion rates hurt your bottom line.',
    solutionTitle: 'High-Converting Lead Capture',
    solutionDescription: 'Create optimized lead generation forms that capture more qualified leads. Route every one into the CRM as a contact and a deal the moment it lands.',
    benefits: [
      { title: 'Higher Conversion', description: 'Optimized forms that convert more visitors' },
      { title: 'Lead Qualification', description: 'Automatically qualify leads with smart questions' },
      { title: 'Instant Follow-up', description: 'Connect to your CRM for immediate follow-up' },
      { title: 'A/B Testing Ready', description: 'Test different form versions to optimize' },
    ],
    exampleFields: ['Company Name', 'Work Email', 'Phone', 'Company Size', 'Budget Range', 'Timeline'],
    features: ['Multi-step forms', 'CRM sync', 'Conditional logic', 'Lead scoring'],
  },
  {
    slug: 'surveys',
    title: 'Surveys',
    subtitle: 'Collect valuable feedback and insights',
    description: 'Create engaging surveys that help you understand your customers better.',
    category: 'business',
    icon: 'ClipboardDocumentListIcon',
    problemTitle: 'Need Better Customer Insights?',
    problemDescription: 'Making decisions without customer feedback is risky. Long, boring surveys get abandoned, leaving you without the data you need.',
    solutionTitle: 'Engaging Surveys That Get Responses',
    solutionDescription: 'Build beautiful surveys that people actually complete. Use progress indicators and smart logic to keep respondents engaged.',
    benefits: [
      { title: 'Higher Completion', description: 'Engaging design keeps respondents interested' },
      { title: 'Smart Logic', description: 'Show relevant questions based on answers' },
      { title: 'Analytics', description: 'Visualize responses with built-in analytics' },
      { title: 'Export Data', description: 'Export results to CSV or integrate with tools' },
    ],
    exampleFields: ['Rating Scale', 'Multiple Choice', 'Open Text', 'Matrix Questions', 'Net Promoter Score'],
    features: ['Progress bar', 'Skip logic', 'Anonymous responses', 'Data export'],
  },
  {
    slug: 'feedback',
    title: 'Feedback Forms',
    subtitle: 'Gather actionable customer feedback',
    description: 'Create feedback forms that help you improve your products and services.',
    category: 'business',
    icon: 'ChatBubbleLeftRightIcon',
    problemTitle: 'Not Hearing From Your Customers?',
    problemDescription: 'Customers have opinions about your business, but most won\'t share unless you make it easy. Missing feedback means missing opportunities to improve.',
    solutionTitle: 'Easy Feedback Collection',
    solutionDescription: 'Create simple feedback forms that encourage customers to share their thoughts. Get actionable insights to improve your business.',
    benefits: [
      { title: 'Quick & Easy', description: 'Simple forms customers actually complete' },
      { title: 'Real-time Alerts', description: 'Get notified of negative feedback instantly' },
      { title: 'Trend Analysis', description: 'Track feedback trends over time' },
      { title: 'Follow-up Ready', description: 'Respond to feedback and close the loop' },
    ],
    exampleFields: ['Overall Rating', 'What did you like?', 'What could be better?', 'Would you recommend us?', 'Additional Comments'],
    features: ['Star ratings', 'Emoji reactions', 'Follow-up questions', 'Sentiment analysis'],
  },
  {
    slug: 'event-registration',
    title: 'Event Registration',
    subtitle: 'Streamline your event sign-ups',
    description: 'Create registration forms that make event management simple.',
    category: 'business',
    icon: 'CalendarDaysIcon',
    problemTitle: 'Event Management Headaches?',
    problemDescription: 'Managing event registrations manually is time-consuming and error-prone. Spreadsheets get messy, and you risk overbooking.',
    solutionTitle: 'Seamless Event Registration',
    solutionDescription: 'Create professional registration forms with capacity limits, session selection, and automatic confirmations.',
    benefits: [
      { title: 'Capacity Management', description: 'Set limits and close registration automatically' },
      { title: 'Session Selection', description: 'Let attendees choose sessions or workshops' },
      { title: 'Confirmations', description: 'Send automatic confirmation emails' },
      { title: 'Attendee List', description: 'Export attendee lists for check-in' },
    ],
    exampleFields: ['Name', 'Email', 'Organization', 'Dietary Requirements', 'Session Preferences', 'Special Needs'],
    features: ['Capacity limits', 'Waitlist', 'Calendar integration', 'Ticket types'],
  },

  // Industry Use Cases
  {
    slug: 'real-estate',
    title: 'Real Estate',
    subtitle: 'Capture property inquiries and showing requests',
    description: 'Forms designed for real estate professionals to capture leads and schedule showings.',
    category: 'industry',
    icon: 'HomeIcon',
    problemTitle: 'Missing Property Inquiries?',
    problemDescription: 'Buyers move fast in real estate. Slow response times or missed inquiries mean lost deals to competitors.',
    solutionTitle: 'Real Estate Lead Capture',
    solutionDescription: 'Create property inquiry forms that capture buyer interest and schedule showings automatically.',
    benefits: [
      { title: 'Instant Alerts', description: 'Get notified immediately of new inquiries' },
      { title: 'Property Matching', description: 'Capture buyer preferences for matching' },
      { title: 'Showing Scheduler', description: 'Let buyers request showing times' },
      { title: 'Lead Organization', description: 'Keep all inquiries organized by property' },
    ],
    exampleFields: ['Name', 'Email', 'Phone', 'Property Interest', 'Pre-approved?', 'Preferred Showing Times', 'Budget Range'],
    features: ['Property selection', 'Calendar integration', 'Agent assignment', 'Follow-up automation'],
  },
  {
    slug: 'healthcare',
    title: 'Healthcare',
    subtitle: 'Patient intake and appointment requests',
    description: 'HIPAA-friendly forms for healthcare providers to collect patient information.',
    category: 'industry',
    icon: 'HeartIcon',
    problemTitle: 'Paperwork Slowing You Down?',
    problemDescription: 'Manual patient intake is slow and error-prone. Handwritten forms are hard to read and require manual data entry.',
    solutionTitle: 'Digital Patient Intake',
    solutionDescription: 'Create secure digital intake forms that patients can complete before their visit. Save time and reduce errors.',
    benefits: [
      { title: 'Pre-visit Completion', description: 'Patients complete forms before arriving' },
      { title: 'Secure & Private', description: 'SSL encryption protects patient data' },
      { title: 'Reduced Wait Times', description: 'Streamline check-in with pre-filled data' },
      { title: 'Accurate Records', description: 'Eliminate handwriting interpretation errors' },
    ],
    exampleFields: ['Patient Name', 'Date of Birth', 'Insurance Info', 'Medical History', 'Current Medications', 'Allergies', 'Reason for Visit'],
    features: ['Secure submission', 'E-signature', 'File upload', 'Conditional sections'],
  },
  {
    slug: 'education',
    title: 'Education',
    subtitle: 'Course enrollment and student feedback',
    description: 'Forms for educational institutions to manage enrollments and collect feedback.',
    category: 'industry',
    icon: 'AcademicCapIcon',
    problemTitle: 'Administrative Overload?',
    problemDescription: 'Managing course registrations, permission slips, and feedback manually takes time away from education.',
    solutionTitle: 'Streamlined Education Forms',
    solutionDescription: 'Create enrollment forms, permission slips, and feedback surveys that reduce administrative burden.',
    benefits: [
      { title: 'Easy Enrollment', description: 'Students and parents can enroll online' },
      { title: 'Permission Management', description: 'Digital permission slips with e-signatures' },
      { title: 'Course Feedback', description: 'Collect anonymous course evaluations' },
      { title: 'Data Organization', description: 'Keep student information organized' },
    ],
    exampleFields: ['Student Name', 'Grade Level', 'Parent/Guardian', 'Emergency Contact', 'Course Selection', 'Medical Info'],
    features: ['Parent signatures', 'Course selection', 'Payment integration', 'Bulk export'],
  },
  {
    slug: 'ecommerce',
    title: 'E-commerce',
    subtitle: 'Product inquiries and custom orders',
    description: 'Forms for online stores to handle inquiries, custom orders, and returns.',
    category: 'industry',
    icon: 'ShoppingCartIcon',
    problemTitle: 'Losing Custom Order Revenue?',
    problemDescription: 'Not every sale fits a standard product page. Custom orders, bulk inquiries, and special requests need a different approach.',
    solutionTitle: 'Custom Order Forms',
    solutionDescription: 'Create product inquiry and custom order forms that capture exactly what customers need.',
    benefits: [
      { title: 'Custom Orders', description: 'Capture detailed specifications for custom products' },
      { title: 'Bulk Inquiries', description: 'Handle wholesale and bulk order requests' },
      { title: 'Return Requests', description: 'Streamline the return process' },
      { title: 'Product Questions', description: 'Answer pre-sale questions efficiently' },
    ],
    exampleFields: ['Product Name', 'Quantity', 'Customization Details', 'Delivery Date', 'Budget', 'Contact Info'],
    features: ['Product selection', 'File upload', 'Price calculation', 'Order confirmation'],
  },
  {
    slug: 'hr-recruiting',
    title: 'HR & Recruiting',
    subtitle: 'Job applications and employee surveys',
    description: 'Forms for HR teams to manage applications, onboarding, and employee feedback.',
    category: 'industry',
    icon: 'BriefcaseIcon',
    problemTitle: 'Hiring Process Chaos?',
    problemDescription: 'Managing job applications across email, LinkedIn, and job boards is messy. Important candidates slip through the cracks.',
    solutionTitle: 'Streamlined HR Forms',
    solutionDescription: 'Create job application forms, onboarding checklists, and employee surveys in one place.',
    benefits: [
      { title: 'Centralized Applications', description: 'All applications in one organized place' },
      { title: 'Resume Upload', description: 'Candidates can attach resumes and portfolios' },
      { title: 'Screening Questions', description: 'Filter candidates with qualifying questions' },
      { title: 'Employee Surveys', description: 'Collect anonymous employee feedback' },
    ],
    exampleFields: ['Full Name', 'Email', 'Phone', 'Position Applied', 'Resume Upload', 'Cover Letter', 'Availability', 'Salary Expectations'],
    features: ['File attachments', 'Screening questions', 'Anonymous surveys', 'Applicant tracking'],
  },

  // Form Type Use Cases
  {
    slug: 'registration-forms',
    title: 'Registration Forms',
    subtitle: 'User and member sign-ups made easy',
    description: 'Create registration forms for memberships, accounts, and subscriptions.',
    category: 'form-type',
    icon: 'UserCircleIcon',
    problemTitle: 'Registration Drop-offs?',
    problemDescription: 'Complicated registration forms cause potential members to abandon sign-up. Every field you add increases drop-off rates.',
    solutionTitle: 'Optimized Registration',
    solutionDescription: 'Create streamlined registration forms that capture essential info without overwhelming users.',
    benefits: [
      { title: 'Minimal Friction', description: 'Only ask for what you really need' },
      { title: 'Smart Defaults', description: 'Pre-fill fields where possible' },
      { title: 'Progress Saving', description: 'Users can save and continue later' },
      { title: 'Confirmation', description: 'Send automatic welcome emails' },
    ],
    exampleFields: ['Username', 'Email', 'Password', 'Full Name', 'Phone (optional)', 'How did you hear about us?'],
    features: ['Password validation', 'Email verification', 'Terms acceptance', 'Welcome emails'],
  },
  {
    slug: 'order-forms',
    title: 'Order Forms',
    subtitle: 'Product and service orders simplified',
    description: 'Create order forms for products, services, and subscriptions.',
    category: 'form-type',
    icon: 'ShoppingBagIcon',
    problemTitle: 'Complex Order Process?',
    problemDescription: 'Not every order fits a standard checkout. Custom products, services, and subscriptions need flexible order forms.',
    solutionTitle: 'Flexible Order Forms',
    solutionDescription: 'Create custom order forms with quantity selection, options, and automatic calculations.',
    benefits: [
      { title: 'Product Options', description: 'Let customers choose sizes, colors, etc.' },
      { title: 'Quantity Selection', description: 'Handle single or bulk orders' },
      { title: 'Auto Calculations', description: 'Show totals based on selections' },
      { title: 'Order Confirmation', description: 'Send automatic order confirmations' },
    ],
    exampleFields: ['Product Selection', 'Quantity', 'Size/Color', 'Delivery Address', 'Special Instructions', 'Payment Method'],
    features: ['Product catalog', 'Price calculation', 'Inventory check', 'Order notifications'],
  },
  {
    slug: 'application-forms',
    title: 'Application Forms',
    subtitle: 'Streamline any application process',
    description: 'Create application forms for programs, grants, memberships, and more.',
    category: 'form-type',
    icon: 'DocumentTextIcon',
    problemTitle: 'Application Management Chaos?',
    problemDescription: 'Processing applications manually is time-consuming. Important details get lost, and applicants wait too long for responses.',
    solutionTitle: 'Professional Application Forms',
    solutionDescription: 'Create comprehensive application forms with document uploads, multi-step sections, and automatic acknowledgments.',
    benefits: [
      { title: 'Document Collection', description: 'Applicants can upload required documents' },
      { title: 'Multi-step Process', description: 'Break long applications into sections' },
      { title: 'Status Updates', description: 'Keep applicants informed automatically' },
      { title: 'Review Workflow', description: 'Organize applications for easy review' },
    ],
    exampleFields: ['Applicant Name', 'Contact Info', 'Background Information', 'Statement of Purpose', 'References', 'Supporting Documents'],
    features: ['File uploads', 'Multi-page forms', 'Save & continue', 'Application status'],
  },
  {
    slug: 'support-tickets',
    title: 'Support Tickets',
    subtitle: 'Customer support requests organized',
    description: 'Create support ticket forms that help you resolve issues faster.',
    category: 'form-type',
    icon: 'LifebuoyIcon',
    problemTitle: 'Support Request Chaos?',
    problemDescription: 'Support emails lack structure, making it hard to prioritize and route issues. Important details are missing, requiring back-and-forth.',
    solutionTitle: 'Structured Support Tickets',
    solutionDescription: 'Create support forms that capture all needed info upfront. Route tickets automatically based on issue type.',
    benefits: [
      { title: 'Complete Info', description: 'Get all details in the first submission' },
      { title: 'Priority Setting', description: 'Let users indicate urgency' },
      { title: 'Auto Routing', description: 'Route to the right team automatically' },
      { title: 'Ticket Tracking', description: 'Customers can track their requests' },
    ],
    exampleFields: ['Name', 'Email', 'Issue Category', 'Priority Level', 'Description', 'Screenshots/Attachments', 'Order Number'],
    features: ['Category routing', 'File attachments', 'Ticket numbers', 'Auto-responses'],
  },
  {
    slug: 'quote-requests',
    title: 'Quote Requests',
    subtitle: 'Service estimates and proposals',
    description: 'Create quote request forms that capture project details for accurate estimates.',
    category: 'form-type',
    icon: 'CalculatorIcon',
    problemTitle: 'Incomplete Quote Requests?',
    problemDescription: 'Vague quote requests waste time with back-and-forth emails. Without proper details, you can\'t provide accurate estimates.',
    solutionTitle: 'Detailed Quote Forms',
    solutionDescription: 'Create quote request forms that capture all project details upfront. Provide accurate estimates faster.',
    benefits: [
      { title: 'Complete Details', description: 'Get all project info in one submission' },
      { title: 'Faster Response', description: 'Provide accurate quotes quickly' },
      { title: 'Professional Image', description: 'Structured process builds trust' },
      { title: 'Lead Qualification', description: 'Identify serious prospects' },
    ],
    exampleFields: ['Company Name', 'Contact Info', 'Project Type', 'Project Description', 'Budget Range', 'Timeline', 'Additional Requirements'],
    features: ['Service selection', 'File attachments', 'Budget ranges', 'Auto-acknowledgment'],
  },

  // Business Type Use Cases
  {
    slug: 'small-business',
    title: 'Small Business',
    subtitle: 'Grow your business with smart forms and CRM',
    description: 'Complete form and CRM solution designed for small business needs - from lead capture to deal tracking.',
    category: 'business-type',
    icon: 'BuildingStorefrontIcon',
    problemTitle: 'Struggling to Manage Growth?',
    problemDescription: 'As a small business owner, you wear many hats. Managing leads across spreadsheets, sticky notes, and email is chaotic. Important follow-ups slip through the cracks.',
    solutionTitle: 'All-in-One Business Growth',
    solutionDescription: 'Instaform combines lead capture forms with a lightweight CRM. Capture leads, create contacts, track deals, and manage tasks - all in one place.',
    benefits: [
      { title: 'Lead Capture', description: 'Beautiful forms that convert website visitors into leads' },
      { title: 'Contact Management', description: 'Organize customers and prospects in one place' },
      { title: 'Deal Pipeline', description: 'Track opportunities from first contact to closed deal' },
      { title: 'Task Management', description: 'Never miss a follow-up with task reminders' },
    ],
    exampleFields: ['Contact Form', 'Quote Request', 'Appointment Booking', 'Customer Feedback', 'Newsletter Signup'],
    features: ['Lead capture forms', 'CRM contacts', 'Deal tracking', 'Task management', 'Email notifications'],
    strategies: [
      { title: 'Capture Every Lead', description: 'Add contact forms to your website, social media, and email signatures. Every touchpoint is an opportunity.' },
      { title: 'Qualify Leads Automatically', description: 'Ask the qualifying questions on the form itself, then sort the answers in the CRM instead of in your inbox.' },
      { title: 'Follow Up Fast', description: 'Set up task reminders to follow up within 24 hours. Speed wins deals.' },
      { title: 'Track Your Pipeline', description: 'Move deals through stages from Lead to Won. Know exactly where every opportunity stands.' },
    ],
  },
  {
    slug: 'sole-proprietor',
    title: 'Sole Proprietorship',
    subtitle: 'Simplify client management as a one-person business',
    description: 'Perfect for freelancers and solo entrepreneurs who need professional forms without enterprise complexity.',
    category: 'business-type',
    icon: 'UserIcon',
    problemTitle: 'Doing Everything Yourself?',
    problemDescription: 'Running a business alone means every minute counts. Complex tools eat up time you could spend on actual work or finding new clients.',
    solutionTitle: 'Simple Yet Powerful',
    solutionDescription: 'Instaform gives you professional tools without the complexity. Create forms, manage clients, and track projects - all designed for one-person operations.',
    benefits: [
      { title: 'Professional Image', description: 'Branded forms that make you look established' },
      { title: 'Client Intake', description: 'Streamlined onboarding for new clients' },
      { title: 'Simple CRM', description: 'Just enough features without overwhelm' },
      { title: 'Time Saver', description: 'Automate repetitive admin tasks' },
    ],
    exampleFields: ['Client Intake Form', 'Project Brief', 'Invoice Request', 'Testimonial Request', 'Scheduling Form'],
    features: ['Client intake forms', 'Simple contact list', 'Project notes', 'Task reminders', 'Mobile friendly'],
    strategies: [
      { title: 'Automate Client Intake', description: 'Send new clients a comprehensive intake form before your first meeting. Arrive prepared and professional.' },
      { title: 'Request Testimonials', description: 'Create a simple form to collect client testimonials. Social proof helps win more business.' },
      { title: 'Streamline Communication', description: 'Use forms for project briefs and feedback instead of back-and-forth emails.' },
      { title: 'Stay Organized', description: 'Keep all client info and project notes in one place. Never scramble for details again.' },
    ],
  },
  {
    slug: 'artists',
    title: 'Artists & Creatives',
    subtitle: 'Manage commissions and client relationships',
    description: 'Built for artists, designers, and creatives who need to handle commission requests and client projects professionally.',
    category: 'business-type',
    icon: 'PaintBrushIcon',
    problemTitle: 'Commission Chaos?',
    problemDescription: 'Managing commission requests through DMs, emails, and comments is messy. Details get lost, and you waste time asking the same questions repeatedly.',
    solutionTitle: 'Professional Commission Management',
    solutionDescription: 'Create commission request forms that capture all project details upfront. Track clients, deadlines, and payments in one organized system.',
    benefits: [
      { title: 'Clear Requirements', description: 'Detailed commission forms eliminate back-and-forth' },
      { title: 'Portfolio Inquiries', description: 'Capture interest from potential clients' },
      { title: 'Project Tracking', description: 'Track commissions from request to delivery' },
      { title: 'Client Records', description: 'Keep client history for repeat business' },
    ],
    exampleFields: ['Commission Type', 'Project Description', 'Reference Images', 'Budget', 'Deadline', 'Usage Rights', 'Contact Info'],
    features: ['File uploads', 'Commission tracking', 'Client notes', 'Deadline reminders', 'Payment status'],
    strategies: [
      { title: 'Standardize Requests', description: 'Create commission tiers with clear pricing. Let clients select what they want upfront.' },
      { title: 'Collect References', description: 'Use file upload fields to gather reference images and mood boards with the request.' },
      { title: 'Set Expectations', description: 'Include terms and turnaround times in your form. Avoid scope creep and misunderstandings.' },
      { title: 'Build Repeat Business', description: 'Keep client records to offer personalized service. Remember their preferences and past projects.' },
    ],
  },
  {
    slug: 'consultants',
    title: 'Consultants',
    subtitle: 'Streamline client acquisition and project management',
    description: 'Professional tools for consultants to capture leads, qualify prospects, and manage client engagements.',
    category: 'business-type',
    icon: 'PresentationChartLineIcon',
    problemTitle: 'Losing Track of Opportunities?',
    problemDescription: 'As a consultant, your pipeline is everything. Unqualified leads waste time, and without systematic follow-up, good prospects go cold.',
    solutionTitle: 'Consulting Practice Growth',
    solutionDescription: 'Qualify leads with smart intake forms, track proposals through your pipeline, and manage ongoing client relationships - all in one system.',
    benefits: [
      { title: 'Lead Qualification', description: 'Forms that identify ideal clients automatically' },
      { title: 'Discovery Calls', description: 'Booking forms that gather context beforehand' },
      { title: 'Proposal Tracking', description: 'Track proposals from sent to signed' },
      { title: 'Client Portal', description: 'Professional intake for new engagements' },
    ],
    exampleFields: ['Company Name', 'Industry', 'Challenge Description', 'Budget Range', 'Timeline', 'Decision Maker?', 'Preferred Meeting Times'],
    features: ['Discovery call booking', 'Proposal pipeline', 'Client onboarding', 'Project notes', 'Lead scoring'],
    strategies: [
      { title: 'Qualify Before the Call', description: 'Send an intake form before discovery calls. Know their budget, timeline, and challenges in advance.' },
      { title: 'Track Proposal Stages', description: 'Use deal stages: Prospect, Proposal Sent, Negotiating, Won. Know where every opportunity stands.' },
      { title: 'Structured Onboarding', description: 'Create client onboarding forms to gather everything needed to start the engagement smoothly.' },
      { title: 'Document Everything', description: 'Keep detailed notes on each client. Reference past conversations and recommendations.' },
    ],
  },
  {
    slug: 'contractors',
    title: 'Independent Contractors',
    subtitle: 'Manage quotes, jobs, and client relationships',
    description: 'Perfect for contractors, tradespeople, and service providers who need to handle quote requests and job tracking.',
    category: 'business-type',
    icon: 'WrenchScrewdriverIcon',
    problemTitle: 'Quote Requests All Over the Place?',
    problemDescription: 'Phone calls, texts, emails - quote requests come from everywhere. Details get lost, and you spend hours creating estimates from incomplete information.',
    solutionTitle: 'Streamlined Job Management',
    solutionDescription: 'Professional quote request forms capture all job details upfront. Track jobs from quote to completion, and build a client database for repeat business.',
    benefits: [
      { title: 'Complete Job Details', description: 'Quote forms that capture everything you need' },
      { title: 'Photo Uploads', description: 'Clients can share photos of the job site' },
      { title: 'Job Tracking', description: 'Track jobs from quote to final invoice' },
      { title: 'Client History', description: 'Remember past jobs for returning customers' },
    ],
    exampleFields: ['Job Type', 'Property Address', 'Job Description', 'Photos of Work Area', 'Preferred Timeline', 'Budget', 'Contact Info'],
    features: ['Quote request forms', 'Photo uploads', 'Job pipeline', 'Client records', 'Follow-up reminders'],
    strategies: [
      { title: 'Get Complete Information', description: 'Require photos and detailed descriptions in quote requests. Provide accurate estimates without extra site visits.' },
      { title: 'Track Job Stages', description: 'Use pipeline stages: Quote Sent, Approved, Scheduled, In Progress, Complete, Paid.' },
      { title: 'Follow Up on Quotes', description: 'Set task reminders to follow up on sent quotes. Many jobs are won with timely follow-up.' },
      { title: 'Build Repeat Business', description: 'Keep records of completed jobs. Reach out to past clients for seasonal maintenance or new projects.' },
    ],
  },

  // Link Page Use Cases
  {
    slug: 'link-in-bio-influencers',
    title: 'Link in Bio for Influencers & Content Creators',
    subtitle: 'One link for all your content, collabs, and merch',
    description: 'Create a free link in bio page for Instagram that showcases your content, brand partnerships, merch store, and contact form. The perfect bio link page for influencers and content creators.',
    category: 'link-page',
    icon: 'SparklesIcon',
    problemTitle: 'One Link Isn\'t Enough for Your Brand',
    problemDescription: 'Instagram only gives you one bio link. You need to share YouTube videos, TikTok, merch, sponsorship inquiries, and more. Switching your link every time you post something new means followers miss out.',
    solutionTitle: 'Your All-in-One Bio Link Page',
    solutionDescription: 'Create a customizable link in bio page that houses all your content and partnerships. Add links to your latest video, merch store, brand inquiry form, and social profiles — all from one link.',
    benefits: [
      { title: 'Showcase Everything', description: 'Links to YouTube, TikTok, podcast, merch, and more in one place' },
      { title: 'Brand Inquiry Form', description: 'Built-in contact form so brands can reach you directly' },
      { title: 'Track Clicks', description: 'See which links your followers click most' },
      { title: 'Custom Branding', description: 'Match your bio link page to your personal brand aesthetic' },
    ],
    exampleFields: ['Latest YouTube Video', 'Merch Store', 'Brand Collaboration Form', 'Newsletter Signup', 'Social Profiles'],
    features: ['Custom themes', 'Click analytics', 'Embedded forms', 'Social icons', 'Scheduling links'],
    strategies: [
      { title: 'Pin Your Best Content', description: 'Keep your highest-performing content at the top. Update links when you post new videos or collaborations.' },
      { title: 'Add a Brand Inquiry Form', description: 'Make it easy for brands to pitch partnerships. A professional inquiry form signals you\'re serious about collaborations.' },
      { title: 'Promote Your Merch', description: 'Link directly to your store or product pages. Place merch links where followers will see them first.' },
      { title: 'Grow Your Email List', description: 'Add a newsletter signup form to capture your audience outside of social media algorithms.' },
    ],
  },
  {
    slug: 'link-in-bio-musicians',
    title: 'Link in Bio for Musicians & DJs',
    subtitle: 'Share your music, shows, and booking info from one link',
    description: 'Build a free link in bio page for Instagram designed for musicians and DJs. Share streaming links, tour dates, booking inquiries, and merch — all from your Instagram bio link.',
    category: 'link-page',
    icon: 'MusicalNoteIcon',
    problemTitle: 'Your Music Is Everywhere — Your Link Isn\'t',
    problemDescription: 'Your tracks are on Spotify, Apple Music, SoundCloud, YouTube, and Bandcamp. Fans need to find your shows, merch, and booking info. One link can\'t cover all of that.',
    solutionTitle: 'One Link for Your Entire Music Career',
    solutionDescription: 'Create a bio link page that connects fans to all your streaming platforms, tour dates, booking form, and merch store. Update it in seconds when you drop new music.',
    benefits: [
      { title: 'All Streaming Platforms', description: 'Link to Spotify, Apple Music, SoundCloud, and more' },
      { title: 'Booking Form', description: 'Let venues and promoters book you directly' },
      { title: 'Tour Dates', description: 'Promote upcoming shows and ticket links' },
      { title: 'Merch Sales', description: 'Drive fans to your merchandise store' },
    ],
    exampleFields: ['Latest Single/Album', 'Spotify Profile', 'Tour Dates', 'Booking Inquiry Form', 'Merch Store'],
    features: ['Music embeds', 'Event links', 'Booking forms', 'Social icons', 'Custom themes'],
    strategies: [
      { title: 'Lead with Your Latest Release', description: 'Put your newest single or album at the top. Update it every time you drop new music.' },
      { title: 'Make Booking Easy', description: 'Add a booking inquiry form with fields for event type, date, budget, and venue. Venues book artists who make it simple.' },
      { title: 'Promote Shows Strategically', description: 'Add upcoming show links with ticket URLs. Remove past shows to keep the page clean.' },
      { title: 'Cross-Promote Platforms', description: 'Link all streaming platforms so fans can listen wherever they prefer. More streams means more revenue.' },
    ],
  },
  {
    slug: 'link-in-bio-fitness',
    title: 'Link in Bio for Fitness Coaches',
    subtitle: 'Connect clients to programs, bookings, and free content',
    description: 'Create a link in bio page for Instagram tailored for fitness coaches and personal trainers. Share workout programs, class schedules, client intake forms, and testimonials from one bio link.',
    category: 'link-page',
    icon: 'FireIcon',
    problemTitle: 'Clients Can\'t Find Your Programs',
    problemDescription: 'You post workouts on Instagram, sell programs on your site, take bookings via DM, and share free content on YouTube. Potential clients get lost trying to find what they need.',
    solutionTitle: 'Your Fitness Hub in One Link',
    solutionDescription: 'Build a bio link page that organizes your programs, class schedule, client intake form, and free resources. Potential clients find exactly what they need in seconds.',
    benefits: [
      { title: 'Program Sales', description: 'Link directly to your workout programs and courses' },
      { title: 'Client Intake', description: 'Embedded form to collect new client info and goals' },
      { title: 'Class Schedule', description: 'Share booking links for in-person or virtual sessions' },
      { title: 'Free Content', description: 'Showcase YouTube workouts and nutrition guides' },
    ],
    exampleFields: ['Online Programs', 'Book a Session', 'Client Intake Form', 'Free Workout Guide', 'Testimonials'],
    features: ['Scheduling links', 'Intake forms', 'Click analytics', 'Custom themes', 'Social icons'],
    strategies: [
      { title: 'Offer a Free Lead Magnet', description: 'Link a free workout PDF or nutrition guide to collect emails. Convert followers into paying clients over time.' },
      { title: 'Streamline Client Intake', description: 'Add an intake form asking about fitness goals, injuries, and availability. Be prepared before the first session.' },
      { title: 'Promote Seasonal Programs', description: 'Update your top link for seasonal challenges like "New Year Shred" or "Summer Body Bootcamp".' },
      { title: 'Showcase Transformations', description: 'Link to a testimonials page or before/after gallery. Social proof converts browsers into clients.' },
    ],
  },
  {
    slug: 'link-in-bio-restaurants',
    title: 'Link in Bio for Restaurants & Cafes',
    subtitle: 'Menus, reservations, and ordering in one bio link',
    description: 'Create a link in bio page for your restaurant or cafe\'s Instagram. Share your menu, take reservations, enable online ordering, and collect customer feedback from one link.',
    category: 'link-page',
    icon: 'BuildingStorefrontIcon',
    problemTitle: 'Customers Can\'t Find Your Menu or Book a Table',
    problemDescription: 'Hungry customers visit your Instagram bio looking for your menu, reservation link, or delivery options. If they can\'t find it instantly, they scroll to the next restaurant.',
    solutionTitle: 'Everything Your Customers Need — One Tap Away',
    solutionDescription: 'Build a bio link page with your menu, reservation system, online ordering, location map, and feedback form. Customers get what they need without leaving Instagram.',
    benefits: [
      { title: 'Menu Access', description: 'Link to your digital menu or PDF' },
      { title: 'Reservations', description: 'Connect to your booking system for easy reservations' },
      { title: 'Online Ordering', description: 'Link to delivery platforms or your own ordering page' },
      { title: 'Customer Feedback', description: 'Collect reviews and feedback through a simple form' },
    ],
    exampleFields: ['View Menu', 'Make a Reservation', 'Order Online', 'Leave Feedback', 'Find Us (Map)'],
    features: ['Scheduling links', 'Embedded forms', 'Custom themes', 'Social icons', 'Click analytics'],
    strategies: [
      { title: 'Lead with Reservations', description: 'Make the reservation link your top block. Hungry followers want to book, not browse.' },
      { title: 'Highlight Specials', description: 'Add a temporary link for daily specials, happy hour deals, or seasonal menus.' },
      { title: 'Collect Email Signups', description: 'Add a form for VIP list signups. Send exclusive offers and event invites to loyal customers.' },
      { title: 'Gather Feedback', description: 'Add a short feedback form. Positive reviews become social proof; negative ones help you improve.' },
    ],
  },
  {
    slug: 'link-in-bio-shops',
    title: 'Link in Bio for Online Shops',
    subtitle: 'Drive traffic to your products and collections',
    description: 'Create a link in bio page for your online store\'s Instagram. Showcase featured products, new arrivals, sales, and customer support — all from your Instagram bio link.',
    category: 'link-page',
    icon: 'ShoppingCartIcon',
    problemTitle: 'Instagram Followers Aren\'t Becoming Customers',
    problemDescription: 'You post beautiful product photos, but followers can\'t easily find and buy what they see. Your single bio link goes to your homepage where they get lost.',
    solutionTitle: 'Turn Followers into Buyers',
    solutionDescription: 'Create a shoppable bio link page that points followers directly to featured products, new arrivals, active sales, and customer support. Reduce friction between "I want this" and "I bought this".',
    benefits: [
      { title: 'Featured Products', description: 'Link directly to your best-selling or newest products' },
      { title: 'Sale Promotion', description: 'Highlight active sales and discount codes' },
      { title: 'Customer Support', description: 'Add a contact form for order inquiries' },
      { title: 'Track Performance', description: 'See which product links get the most clicks' },
    ],
    exampleFields: ['Shop New Arrivals', 'Best Sellers', 'Current Sale', 'Contact Support', 'Track My Order'],
    features: ['Click analytics', 'Custom themes', 'Embedded forms', 'Social icons', 'Scheduling links'],
    strategies: [
      { title: 'Match Posts to Links', description: 'When you post a product, make sure the matching link is at the top of your bio page. Reduce the gap between interest and purchase.' },
      { title: 'Run Flash Sales', description: 'Add a temporary sale link with urgency. Remove it when the sale ends to keep your page clean.' },
      { title: 'Collect Pre-Orders', description: 'Use an embedded form for pre-orders or waitlists on upcoming products. Build hype before launch.' },
      { title: 'Add Customer Support', description: 'Include an order inquiry form. Happy customers buy again and refer friends.' },
    ],
  },
  {
    slug: 'link-in-bio-photographers',
    title: 'Link in Bio for Photographers',
    subtitle: 'Portfolio, bookings, and prints from one link',
    description: 'Create a link in bio page for your photography Instagram. Showcase your portfolio, accept booking inquiries, sell prints, and share your latest work — all from one bio link page.',
    category: 'link-page',
    icon: 'CameraIcon',
    problemTitle: 'Potential Clients Can\'t Book You Easily',
    problemDescription: 'Your Instagram is your portfolio, but turning followers into paying clients is hard. They DM you, you lose the message, and potential gigs slip away.',
    solutionTitle: 'Professional Booking from Your Bio Link',
    solutionDescription: 'Build a bio link page with your portfolio, booking inquiry form, pricing guide, and print shop. Convert Instagram followers into booked clients with zero DM chaos.',
    benefits: [
      { title: 'Portfolio Link', description: 'Direct followers to your full portfolio website' },
      { title: 'Booking Form', description: 'Professional inquiry form with date, location, and package selection' },
      { title: 'Print Sales', description: 'Link to your print shop or gallery' },
      { title: 'Pricing Guide', description: 'Share your rates to pre-qualify clients' },
    ],
    exampleFields: ['View Portfolio', 'Book a Session', 'Buy Prints', 'Pricing Info', 'Recent Work'],
    features: ['Booking forms', 'Click analytics', 'Custom themes', 'Social icons', 'Image blocks'],
    strategies: [
      { title: 'Lead with Booking', description: 'Put your booking inquiry form front and center. Serious clients want to book, not just browse.' },
      { title: 'Share a Mini Portfolio', description: 'Link to your 3 best galleries. Quality over quantity wins clients.' },
      { title: 'Pre-Qualify with Pricing', description: 'Link to a pricing guide so clients self-select. Spend time on inquiries that match your rates.' },
      { title: 'Sell Prints Passively', description: 'Add a print shop link. Followers who love your work might not need a photographer but will buy a print.' },
    ],
  },
  {
    slug: 'link-in-bio-podcasters',
    title: 'Link in Bio for Podcasters',
    subtitle: 'Episodes, platforms, and guest bookings in one link',
    description: 'Create a link in bio page for your podcast\'s Instagram. Share your latest episodes across all platforms, accept guest pitches, and grow your audience — all from one bio link page.',
    category: 'link-page',
    icon: 'MicrophoneIcon',
    problemTitle: 'Listeners Can\'t Find Your Podcast Easily',
    problemDescription: 'Your podcast is on Apple, Spotify, YouTube, and more. Followers don\'t know which platform to use, and potential guests have no easy way to pitch themselves.',
    solutionTitle: 'One Link for Your Entire Podcast',
    solutionDescription: 'Create a bio link page with links to every platform, your latest episode, guest application form, and newsletter signup. Make it effortless for listeners and guests to connect.',
    benefits: [
      { title: 'All Platforms', description: 'Apple Podcasts, Spotify, YouTube, and more — one tap away' },
      { title: 'Guest Applications', description: 'Structured form for potential guests to pitch topics' },
      { title: 'Latest Episode', description: 'Always promote your newest episode at the top' },
      { title: 'Newsletter Growth', description: 'Collect emails to build an audience you own' },
    ],
    exampleFields: ['Latest Episode', 'Listen on Apple Podcasts', 'Listen on Spotify', 'Apply to Be a Guest', 'Join the Newsletter'],
    features: ['Embedded forms', 'Click analytics', 'Custom themes', 'Social icons', 'Scheduling links'],
    strategies: [
      { title: 'Promote New Episodes Fast', description: 'Update your top link every time you drop an episode. Timely promotion drives downloads.' },
      { title: 'Streamline Guest Booking', description: 'Add a guest application form asking for topic ideas, bio, and social links. Screen guests before committing.' },
      { title: 'Grow Outside Social Media', description: 'Add a newsletter signup form. Build an email list so algorithm changes don\'t tank your reach.' },
      { title: 'Link All Platforms', description: 'Listeners have strong platform preferences. List every platform so nobody bounces because they can\'t find their app.' },
    ],
  },
]

// Merge the authored long-form depth in once, so every consumer
// (useCasesByCategory, getUseCaseBySlug, the localized hooks) sees it.
for (const uc of useCasesData) {
  const depth = USE_CASE_DEPTH[uc.slug]
  if (depth) {
    uc.deepDive = depth.deepDive
    uc.faq = depth.faq
  }
}

export const useCasesByCategory = {
  business: useCasesData.filter((uc) => uc.category === 'business'),
  industry: useCasesData.filter((uc) => uc.category === 'industry'),
  'form-type': useCasesData.filter((uc) => uc.category === 'form-type'),
  'business-type': useCasesData.filter((uc) => uc.category === 'business-type'),
  'link-page': useCasesData.filter((uc) => uc.category === 'link-page'),
}

export const getUseCaseBySlug = (slug: string): UseCaseData | undefined => {
  return useCasesData.find((uc) => uc.slug === slug)
}
